/**
 * Home page. X-Translator is the primary manual; the other guides are secondary.
 */
import React from "react";
import { Link } from "react-router-dom";
import XtLogo from "../components/XtLogo";
import { siteCopy } from "../i18n/siteCopy";
import { useManualLocale } from "../manual/i18n/LocaleContext";

export default function HomePage() {
  const { locale } = useManualLocale();
  const copy = siteCopy[locale];

  const others = [
    {
      id: "x-translator-sw",
      title: copy.swTitle,
      subtitle: copy.swSubtitle,
      text: copy.swText,
      path: "/docs/x-translator-sw",
      available: true,
    },
    {
      id: "aims-dmg",
      title: copy.aimsTitle,
      subtitle: copy.aimsSubtitle,
      text: copy.aimsText,
      path: "/docs/aims-dmg",
      available: true,
    },
    {
      id: "sepm-portal",
      title: copy.portalTitle,
      subtitle: copy.portalSubtitle,
      text: copy.portalText,
      available: false,
    },
  ];

  return (
    <div className="mx-auto max-w-5xl">
      <Link
        to="/docs/x-translator"
        className="group relative flex flex-col gap-6 overflow-hidden rounded-3xl border border-sepm-primary/20 bg-white p-6 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:border-sepm-primary-dark/30 dark:bg-gray-800 sm:flex-row sm:items-center sm:p-8"
      >
        <div
          className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-sepm-primary/10 blur-3xl dark:bg-sepm-primary-dark/15"
          aria-hidden="true"
        />
        <XtLogo className="relative h-24 w-24 shrink-0 sm:h-32 sm:w-32" alt="" />
        <div className="relative min-w-0">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-sepm-primary dark:text-sepm-primary-dark">
            {copy.heroEyebrow}
          </p>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
            {copy.heroTitle}
          </h1>
          <p className="mt-2 max-w-xl text-base text-gray-600 dark:text-gray-300">{copy.heroText}</p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <span className="text-sm font-semibold text-sepm-primary dark:text-sepm-primary-dark">
              {copy.openManual}
            </span>
            <span className="rounded-full bg-sepm-primary/10 px-2.5 py-1 text-xs font-medium text-sepm-primary dark:bg-sepm-primary-dark/10 dark:text-sepm-primary-dark">
              {copy.subscription}
            </span>
          </div>
        </div>
      </Link>

      <h2 className="mt-8 text-xs font-semibold uppercase tracking-[0.16em] text-gray-400 dark:text-gray-500">
        {copy.others}
      </h2>
      <div className="mt-3 grid gap-3 md:grid-cols-3">
        {others.map((app) => {
          const className =
            "flex h-full flex-col rounded-xl border border-gray-200/80 bg-white/70 p-4 text-gray-600 transition dark:border-gray-700/80 dark:bg-gray-800/40 dark:text-gray-400";
          const body = (
            <>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-400 dark:text-gray-500">
                {app.subtitle}
              </p>
              <h3 className="mt-1.5 text-sm font-semibold text-gray-800 dark:text-gray-200">{app.title}</h3>
              <p className="mt-1 flex-1 text-sm leading-relaxed">{app.text}</p>
              <p className="mt-3 text-xs font-medium text-gray-500 dark:text-gray-500">
                {app.available ? copy.open : copy.comingSoon}
              </p>
            </>
          );
          if (!app.available) {
            return (
              <div key={app.id} className={className}>
                {body}
              </div>
            );
          }
          return (
            <Link
              key={app.id}
              to={app.path}
              className={`${className} hover:border-gray-300 hover:bg-white dark:hover:border-gray-600 dark:hover:bg-gray-800`}
            >
              {body}
            </Link>
          );
        })}
      </div>

      <p className="mt-8 text-xs text-gray-400 dark:text-gray-500">{copy.copyright}</p>
    </div>
  );
}
